"use client";

import { useRef, useState } from "react";
import {
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BadgeSheet,
  PlateSheet,
} from "@/components/tools/printables/name-tag-page";
import {
  PaperControls,
  PRINTABLES_COMMON_STRINGS,
  PrintablePages,
  PrintBar,
  useTextMeasure,
} from "@/components/tools/printables/page-kit";
import { useToolUi } from "@/components/tools/tool-ui";
import { ACCENTS, type AccentKey } from "@/lib/tool-kit/printables/certificate";
import {
  badgeGrid,
  parseTagNames,
  plateGrid,
  sheetCount,
  type TagEntry,
  type TagFont,
  type TagKind,
  type TagSize,
  type TagStyle,
} from "@/lib/tool-kit/printables/name-tags";
import {
  type Orientation,
  type PaperSize,
  pageGeometry,
} from "@/lib/tool-kit/printables/page";

/** English strings. A translated content file overrides these through `ui`. */
export const NAME_TAG_GENERATOR_STRINGS = {
  ...PRINTABLES_COMMON_STRINGS,
  sampleNames: "Ava\nBen | Table 2\nChloe\nDiego",
  namesLabel: "Names — one per line",
  namesHint:
    "Add a bar and a second line for a name — for example: Ben | Table 2. Leave the list empty to print blank tags to write on.",
  kindLabel: "Type",
  kinds: {
    badge: "Name tags (stick-on or pinned)",
    plate: "Desk name plates (fold in half)",
  },
  sizeLabel: "Size",
  badgeSizes: {
    small: "Small — 60 × 40 mm (about 2⅜ × 1⅝ in)",
    medium: "Medium — 85 × 55 mm (about 3⅜ × 2⅛ in)",
    large: "Large — 90 × 70 mm (about 3½ × 2¾ in)",
  },
  plateSizes: {
    small: "Small — 60 mm tall when flat, 30 mm folded",
    medium: "Medium — 80 mm tall when flat, 40 mm folded",
    large: "Large — 110 mm tall when flat, 55 mm folded",
  },
  styleLabel: "Design",
  styles: {
    hello: "Coloured band with a greeting",
    classic: "Framed name",
    plain: "Name only",
  },
  headerLabel: "Greeting in the band",
  headerDefault: "Hello, my name is",
  fontLabel: "Lettering",
  fonts: {
    bold: "Bold sans-serif",
    serif: "Bold serif",
    script: "Script — your device's script font",
  },
  accentLabel: "Colour",
  accents: {
    navy: "Navy",
    burgundy: "Burgundy",
    forest: "Forest green",
    gold: "Antique gold",
    purple: "Purple",
    black: "Black and white",
  },
  detailLabel: "Line under every name (optional)",
  detailHint:
    "A class, room or table. A second line typed after a bar replaces it for that name.",
  foldHint:
    "Print, cut along the dashed outline, and fold on the long dashed line. The top half prints upside down so both faces read the right way up when the plate stands.",
  previewLabel: "Name tags preview",
} as const;

type SizeKey = TagSize;

interface Form {
  names: string;
  kind: TagKind;
  size: SizeKey;
  style: TagStyle;
  font: TagFont;
  header: string;
  detail: string;
  accent: AccentKey;
  paper: PaperSize;
  orientation: Orientation;
}

export default function NameTagGeneratorWidget() {
  const strings = useToolUi(NAME_TAG_GENERATOR_STRINGS);
  const measure = useTextMeasure();
  const svgRef = useRef<SVGSVGElement>(null);
  const [form, setForm] = useState<Form>({
    names: strings.sampleNames,
    kind: "badge",
    size: "medium",
    style: "hello",
    font: "bold",
    header: strings.headerDefault,
    detail: "",
    accent: "navy",
    paper: "letter",
    orientation: "portrait",
  });
  const update = (patch: Partial<Form>) =>
    setForm((previous) => ({ ...previous, ...patch }));

  const geometry = pageGeometry(form.paper, form.orientation);
  const grid = form.kind === "badge" ? badgeGrid(geometry, form.size) : null;
  const plates = form.kind === "plate" ? plateGrid(geometry, form.size) : null;
  const perPage = grid ? grid.cells.length : (plates?.plates.length ?? 1);
  const typed = parseTagNames(form.names);
  const blank: TagEntry = { name: "", detail: "" };
  const entries: TagEntry[] =
    typed.length > 0
      ? typed.map((entry) => ({
          name: entry.name,
          detail: entry.detail || form.detail.trim(),
        }))
      : Array.from({ length: perPage }, () => blank);
  const faceOptions = {
    style: form.style,
    font: form.font,
    header: form.header,
  };
  const accent = ACCENTS[form.accent];
  const sheets = Array.from(
    { length: sheetCount(entries.length, perPage) },
    (_, index) => entries.slice(index * perPage, (index + 1) * perPage)
  );
  const pages = sheets.map((tags, index) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: sheets are positional
    <g key={`sheet-${index}`}>
      {grid ? (
        <BadgeSheet
          accent={accent}
          cells={grid.cells}
          measure={measure}
          options={faceOptions}
          tags={tags}
        />
      ) : null}
      {plates ? (
        <PlateSheet
          accent={accent}
          measure={measure}
          options={faceOptions}
          panelHeight={plates.panelHeight}
          plates={plates.plates}
          tags={tags}
        />
      ) : null}
    </g>
  ));
  const sizeStrings =
    form.kind === "badge" ? strings.badgeSizes : strings.plateSizes;

  return (
    <div className="space-y-6 print:m-0 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <TextAreaField
          hint={strings.namesHint}
          id="nt-names"
          label={strings.namesLabel}
          onChange={(names) => update({ names })}
          prose
          rows={6}
          value={form.names}
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SelectField
            id="nt-kind"
            label={strings.kindLabel}
            onChange={(kind) => update({ kind })}
            options={(["badge", "plate"] as const).map((value) => ({
              value,
              label: strings.kinds[value],
            }))}
            value={form.kind}
          />
          <SelectField
            id="nt-size"
            label={strings.sizeLabel}
            onChange={(size) => update({ size })}
            options={(["small", "medium", "large"] as const).map((value) => ({
              value,
              label: sizeStrings[value],
            }))}
            value={form.size}
          />
          <SelectField
            id="nt-style"
            label={strings.styleLabel}
            onChange={(style) => update({ style })}
            options={(["hello", "classic", "plain"] as const).map((value) => ({
              value,
              label: strings.styles[value],
            }))}
            value={form.style}
          />
          {form.style === "hello" ? (
            <TextField
              id="nt-header"
              label={strings.headerLabel}
              onChange={(header) => update({ header })}
              value={form.header}
            />
          ) : null}
          <SelectField
            id="nt-font"
            label={strings.fontLabel}
            onChange={(font) => update({ font })}
            options={(["bold", "serif", "script"] as const).map((value) => ({
              value,
              label: strings.fonts[value],
            }))}
            value={form.font}
          />
          <SelectField
            id="nt-accent"
            label={strings.accentLabel}
            onChange={(next) => update({ accent: next })}
            options={(Object.keys(ACCENTS) as AccentKey[]).map((value) => ({
              value,
              label: strings.accents[value],
            }))}
            value={form.accent}
          />
          <PaperControls
            idPrefix="nt"
            onOrientation={(orientation) => update({ orientation })}
            onPaper={(paper) => update({ paper })}
            orientation={form.orientation}
            paper={form.paper}
            strings={strings}
          />
        </div>
        <TextField
          hint={strings.detailHint}
          id="nt-detail"
          label={strings.detailLabel}
          onChange={(detail) => update({ detail })}
          value={form.detail}
        />
        {form.kind === "plate" ? (
          <p className="text-muted-foreground text-xs">{strings.foldHint}</p>
        ) : null}
        <PrintBar
          fileName={`name-tags-${form.paper}`}
          pageTotal={pages.length}
          paper={form.paper}
          strings={strings}
          svgRef={svgRef}
        />
      </div>
      <PrintablePages
        className={form.orientation === "landscape" ? "max-w-3xl" : "max-w-xl"}
        geometry={geometry}
        label={strings.previewLabel}
        pages={pages}
        svgRef={svgRef}
      />
    </div>
  );
}
